import { useContext, useEffect, useMemo, useRef } from 'react';
import { faChartLineUp, faFunction, faSquare, faTable } from '@fortawesome/pro-regular-svg-icons';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DropdownMenu from 'components/dropdown-menu';
import { TableIgnoredColumns } from 'components/table/TableConstants';
import { TableContext } from 'components/table/TableContext';
import { identity } from 'lodash';
import { twJoin } from 'tailwind-merge';

interface TableColumnVisibilityProps {
    transformIdToReadable?: (id: string) => string;
}
const TableColumnVisibility = (props: TableColumnVisibilityProps) => {
    const { transformIdToReadable = identity } = props;
    const { table } = useContext(TableContext);
    const ref = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (ref.current) {
            const th = ref.current.closest('th');
            th?.classList.add('!px-0');
            ref.current.parentElement?.classList.add('flex', 'justify-center', 'items-center');
        }
    }, []);

    const unsortedColumns = table.getAllColumns();
    const columnOrder = table.initialState.columnOrder;
    const allColumns = useMemo(
        () =>
            [...unsortedColumns].sort((a, b) => {
                const indexA = columnOrder.indexOf(a.id);
                const indexB = columnOrder.indexOf(b.id);

                if (indexA === -1 && indexB === -1) {
                    return 0;
                }

                if (indexA === -1) {
                    return 1;
                }

                if (indexB === -1) {
                    return -1;
                }

                return indexA - indexB;
            }),
        [unsortedColumns, columnOrder],
    );

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger
                ref={ref}
                className={
                    'flex h-8 w-8 items-center justify-center rounded-md p-4 text-gray-500 hover:bg-gray-200'
                }
            >
                <span className="sr-only">Column visibility</span>
                <FontAwesomeIcon icon={faTable} />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                align={'end'}
                className={
                    'flex max-h-[calc(var(--radix-dropdown-menu-content-available-height)-1rem)] flex-col gap-1.5 p-1.5'
                }
                avoidCollisions={true}
            >
                {allColumns
                    .filter(col => !TableIgnoredColumns.includes(col.id))
                    .map(col => {
                        const isVisible = col.getIsVisible();
                        const isDisabled = !col.getCanHide();
                        const isAggregate = !!col.columnDef.meta?.aggregate;
                        const isChartable = !!col.columnDef.meta?.visualize;

                        return (
                            <DropdownMenu.CheckboxItem
                                key={col.id}
                                className={'select-none px-2'}
                                checked={isVisible}
                                disabled={isDisabled}
                                onCheckedChange={checked => {
                                    col.toggleVisibility(checked);

                                    if (!columnOrder.includes(col.id)) {
                                        table.setColumnOrder(state => [...state, col.id]);
                                    }

                                    if (!checked) {
                                        table.options.meta?.onHideColumn?.(col, table);
                                    }
                                }}
                            >
                                <FontAwesomeIcon
                                    className={twJoin(
                                        isDisabled && 'text-gray-300',
                                        'text-blue-500',
                                    )}
                                    icon={isVisible ? faSquareCheck : faSquare}
                                />
                                <div className={'flex items-center justify-between gap-2'}>
                                    <p
                                        className={twJoin(
                                            isDisabled && 'text-gray-600',
                                            'max-w-56 truncate text-sm font-medium',
                                        )}
                                    >
                                        {transformIdToReadable(col.id)}
                                    </p>
                                    {isAggregate && (
                                        <FontAwesomeIcon
                                            size={'sm'}
                                            className={'text-gray-400'}
                                            icon={faFunction}
                                        />
                                    )}
                                    {isChartable && (
                                        <FontAwesomeIcon
                                            size={'sm'}
                                            className={'text-gray-400'}
                                            icon={faChartLineUp}
                                        />
                                    )}
                                </div>
                            </DropdownMenu.CheckboxItem>
                        );
                    })}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
};

export { TableColumnVisibility };
export type { TableColumnVisibilityProps };
